import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Card, CardContent, Grid, MenuItem, TextField, Button, Chip, Typography,
  Alert, LinearProgress,
} from "@mui/material";

import api from "../api";
import { MONO, tokens } from "../theme";
import { PageHeader, Eyebrow } from "../components/ui";
import LabelChips from "../components/LabelChips";

const QUICK_STARTS = [
  { name: "Sentiment", sequence: "The service was absolutely fantastic and exceeded my expectations.",
    labels: ["positive", "negative", "neutral"] },
  { name: "Intent", sequence: "I want to cancel my subscription and get a refund.",
    labels: ["cancel", "refund", "upgrade", "support"] },
  { name: "Topic", sequence: "The new GPU delivers a huge boost in model training throughput.",
    labels: ["technology", "sports", "politics", "finance"] },
  { name: "Urgency", sequence: "Our production server is down and customers can't check out!",
    labels: ["urgent", "normal", "low"] },
];

export default function ClassifierPlayground() {
  const [classifiers, setClassifiers] = useState([]);
  const [selected, setSelected] = useState("");
  const [sequence, setSequence] = useState(QUICK_STARTS[0].sequence);
  const [labels, setLabels] = useState(QUICK_STARTS[0].labels);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [env, setEnv] = useState(null);        // set while the shared venv builds
  const logRef = useRef(null);

  useEffect(() => {
    api.get("/api/classifiers", { silent: true }).then((d) => {
      const enabled = (d.classifiers || []).filter((c) => c.enabled);
      setClassifiers(enabled);
      const pick = enabled.find((c) => c.name === d.default) || enabled[0];
      if (pick) { setSelected(pick.name); if (pick.default_labels?.length) setLabels(pick.default_labels); }
    }).catch(() => {});
  }, []);

  const onPickClassifier = (name) => {
    setSelected(name);
    const c = classifiers.find((x) => x.name === name);
    if (c?.default_labels?.length) setLabels(c.default_labels);
  };
  const quickStart = (q) => { setSequence(q.sequence); setLabels(q.labels); setResults(null); setError(""); };

  const pollEnv = useCallback(() => {
    api.get("/api/classifiers/env", { silent: true }).then((e) => {
      setEnv(e);
      if (e.status === "ready") setEnv(null);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!env || env.status !== "building") return;
    const t = setInterval(pollEnv, 2000);
    return () => clearInterval(t);
  }, [env, pollEnv]);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [env?.log]);

  const classify = async () => {
    if (!selected || !sequence.trim() || !labels.length) return;
    setLoading(true); setError(""); setResults(null);
    try {
      const r = await api.post(`/api/classifier-playground/${selected}/classify`,
        { sequence, labels }, { silent: true });
      setResults(r); setEnv(null);
    } catch (e) {
      // A 409 can mean "env still building" OR a real conflict (e.g. classifier
      // disabled) — check the env before assuming a build is underway.
      if (e.status === 409) {
        let en = null;
        try { en = await api.get("/api/classifiers/env", { silent: true }); } catch { /* ignore */ }
        if (en && en.status === "building") { setEnv(en); pollEnv(); }
        else if (en && en.status === "error") { setEnv(en); }
        else setError(e.message || "Can't classify right now.");
      } else setError(e.message || "Classification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Zero-shot classification"
        title="Classifier playground"
        subtitle="Pick a classifier, give it text and candidate labels, and see the confidence for each."
      />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {QUICK_STARTS.map((q) => (
          <Chip key={q.name} label={q.name} variant="outlined" onClick={() => quickStart(q)} clickable />
        ))}
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card><CardContent>
            <Eyebrow sx={{ mb: 1 }}>Playground</Eyebrow>
            <TextField
              select fullWidth size="small" label="Classifier" value={selected}
              onChange={(e) => onPickClassifier(e.target.value)} sx={{ mb: 1.5 }}
            >
              {classifiers.map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name}{c.model_id ? ` · ${c.model_id}` : ""}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth multiline minRows={5} size="small" label="Text"
              value={sequence} onChange={(e) => setSequence(e.target.value)} sx={{ mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {sequence.trim() ? sequence.trim().split(/\s+/).length : 0} words · {sequence.length} chars
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Candidate labels
            </Typography>
            <Box sx={{ mb: 2 }}>
              <LabelChips labels={labels} onChange={setLabels} />
            </Box>

            <Button
              variant="contained" onClick={classify}
              disabled={loading || !selected || !sequence.trim() || !labels.length}
            >
              {loading ? "Classifying…" : "Classify"}
            </Button>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ minHeight: 260 }}><CardContent>
            <Eyebrow sx={{ mb: 1.5 }}>Results</Eyebrow>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {env?.status === "building" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Building the shared classifier environment (transformers + CPU torch) — this runs once.
                Classify again when it's ready.
                <Box ref={logRef} component="pre" sx={{
                  fontFamily: MONO, fontSize: ".7rem", mt: 1, maxHeight: 160, overflow: "auto",
                  background: "#0d1a2b", color: "#c3d2ea", borderRadius: 1, p: 1, whiteSpace: "pre-wrap",
                }}>{env.log || "…"}</Box>
              </Alert>
            )}

            {env?.status === "error" && (
              <Alert severity="error" sx={{ mb: 2 }}>
                The classifier environment failed to build — open <b>Classifiers</b> and rebuild it.
                {env.log && (
                  <Box component="pre" sx={{
                    fontFamily: MONO, fontSize: ".7rem", mt: 1, maxHeight: 160, overflow: "auto",
                    background: "#0d1a2b", color: "#c3d2ea", borderRadius: 1, p: 1, whiteSpace: "pre-wrap",
                  }}>{env.log}</Box>
                )}
              </Alert>
            )}

            {results && (
              <Box>
                {results.labels.map((label, i) => {
                  const score = results.scores[i] ?? 0;
                  const pct = (score * 100).toFixed(1);
                  const winner = i === 0;
                  return (
                    <Box key={label} sx={{ mb: 1.25 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.35 }}>
                        <Typography sx={{ fontSize: ".85rem", fontWeight: winner ? 700 : 500,
                          color: winner ? tokens.signal : "text.primary" }}>
                          {winner ? "🏆 " : ""}{label}
                        </Typography>
                        <Typography sx={{ fontFamily: MONO, fontSize: ".8rem",
                          color: winner ? tokens.signal : "text.secondary" }}>{pct}%</Typography>
                      </Box>
                      <Box sx={{ height: 8, borderRadius: 4, background: "#eaf0f8", overflow: "hidden" }}>
                        <Box sx={{
                          height: "100%", width: `${pct}%`, borderRadius: 4,
                          background: winner ? tokens.vramGradient : "#c3d2ea",
                          transition: "width .4s ease",
                        }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {!results && !loading && !error && !env && (
              <Typography variant="body2" color="text.secondary">
                Results appear here after you classify.
              </Typography>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>
    </Box>
  );
}
